import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreadCrumb } from '../../UI-Lib/Bread-Crumb/breadCrumbs.component';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import { HelpService } from '../../UI-Lib/Help-Tooltip';
import { PAYMENT_BATCH_COMMAND_CENTER_HELP } from '../Command-Center/paymentBatch.component.help';
import { PaymentBatchService } from '../paymentBatch.service';
import { BusyIndicatorService, IBusyIndicatorConfig } from '../../Busy-Indicator';
import { TaskService } from '../../Task/task.service.upgrade';
import { LaunchTaskModalService } from '../../Task/launchTaskModal.service';
import { AttachmentModalData } from '../../Attachment/attachment.modal.model';
import { CommentModalData } from '../../Comments/comments.service';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { PaymentBatchPaymentsGridComponent } from './Payments-Grid/paymentBatchPaymentsGrid.component';
import { EntityTypeIds } from '../../constants.new';
import { ToastrService } from 'ngx-toastr';
import { PaymentBatchTaskExceptionModalComponent } from './Task-Exception-Modal/paymentBatchTaskExceptionModal.component';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import * as _ from 'lodash';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { uniqBy } from 'lodash';
import { PaymentBatchPackagesComponent } from './Packages-Table/paymentBatchPackages.component';
import { Subject, takeUntil } from 'rxjs';
import { LongRunningProcessService } from '../../Compliance/Long-Running-Process/longRunningProcess.service';
import { FeatureFlagsService } from '../../Common/FeatureFlags/feature-flags-service';
import { RestrictService } from 'src/app/Common/Permissions/restrict.service';

enum TabIdxs {
    Payments = 0,
    Packages = 1,
    Output = 2,
}

export interface RefreshComponents {
    payments: boolean;
    packages: boolean;
    status: boolean;
}

@Component({
    selector: 'payment-batch-details',
    templateUrl: './paymentBatchDetails.component.html',
    styleUrls: ['./paymentBatchDetails.component.scss'],
    standalone: false,
    // encapsulation: ViewEncapsulation.None
})
export class PaymentBatchDetailsComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _routerService: UpgradeNavigationServiceHandler,
        private readonly _helpService: HelpService,
        private readonly _upgradeNavigationServiceHandler: UpgradeNavigationServiceHandler,
        private readonly _modalService: WeissmanModalService,
        private readonly _taskService: TaskService,
        private readonly _launchTaskModalService: LaunchTaskModalService,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _toastr: ToastrService,
        private readonly _messageModalService: MessageModalService,
        private readonly _longRunningProcessService: LongRunningProcessService,
        private readonly _paymentBatchService: PaymentBatchService,
        private readonly _featureFlagService: FeatureFlagsService,
        private readonly _restrictService: RestrictService,
    ) {}

    @ViewChild('paymentBatchDetailsGrid') paymentBatchDetailsGrid: PaymentBatchPaymentsGridComponent;
    @ViewChild('paymentBatchPackages') paymentBatchPackages: PaymentBatchPackagesComponent;
    @ViewChild('paymentDetailsTabs', { static: false }) paymentDetailsTabs?: TabsetComponent;

    isInitialized: boolean = true;
    pageTitle: string = 'Batch';
    breadcrumbs: BreadCrumb[] = [];
    attachmentModalData: AttachmentModalData;
    commentModalData: CommentModalData;
    hasComments: boolean = false;
    hasAttachments: boolean = false;
    paymentBatch: Core.PaymentBatchModel;
    editingFundedAmount: boolean = false;
    savingFundedAmount: boolean = false;
    workflowStatus: Core.PaymentBatchDetailsWorkflowStatusModel;
    tabIndex: number;
    rowCount: number;
    settingsEditing: boolean = false;
    hasWritePermission: boolean = false;

    private destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        this.breadcrumbs = [
            {
                name: 'Payment Batch Command Center',
                target: 'paymentBatchCommandCenter',
                options: {},
            },
        ];
        if (this._upgradeNavigationServiceHandler.getQuerystringParam('tabIndex'))
            this.tabIndex = JSON.parse(this._upgradeNavigationServiceHandler.getQuerystringParam('tabIndex'));

        this._helpService.setContent(PAYMENT_BATCH_COMMAND_CENTER_HELP);
        await this._loadPaymentBatch();
        await this._loadPaymentBatchDetailsWorkflowStatus();
        this.hasWritePermission = await this._restrictService.hasCompanyPermission(
            this.paymentBatch.companyId,
            Core.AccessRightsEnum.Write,
        );

        if (this.tabIndex) this.paymentDetailsTabs.tabs[this.tabIndex].active = true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    get isTransmittal(): boolean {
        return this.paymentBatch?.batchType === Core.PaymentBatchTypeEnum.Transmittal;
    }

    get isBillPay(): boolean {
        return this.paymentBatch?.batchType === Core.PaymentBatchTypeEnum.BillPay;
    }

    get isApproverOnlyMode(): boolean {
        return this.workflowStatus?.isApproverOnlyMode ?? false;
    }

    get isTaskAdvanceConfirmFunding(): boolean {
        return this.workflowStatus?.taskAdvanceName.toLowerCase() === 'confirm funding';
    }

    get isAdvanceTaskDisabled(): boolean {
        return (
            !this.workflowStatus?.canAdvanceTask ||
            this.rowCount === 0 ||
            (this.isTaskAdvanceConfirmFunding && this.paymentBatch.fundedAmount > this.paymentBatch.paymentAmount)
        );
    }

    launchTaskModal(event: any): void {
        event.stopPropagation();
        this._launchTaskModalService.launchTaskModal(this.paymentBatch.paymentBatchId, EntityTypeIds.PAYMENT_BATCH, true);
    }

    goBack(): void {
        sessionStorage['pbGoBack'] = true;
        this._routerService.go('paymentBatchCommandCenter', null);
    }

    async refreshPage({ payments, packages, status }: RefreshComponents, bypassTabSwitch?: boolean): Promise<void> {
        // Tab switching
        if (packages) {
            if (this.paymentDetailsTabs.tabs[TabIdxs.Packages].active) {
                this.paymentBatchPackages.loadData();
            } else {
                !bypassTabSwitch && (this.paymentDetailsTabs.tabs[TabIdxs.Packages].active = true);
            }
        } else if (payments) {
            !bypassTabSwitch && (this.paymentDetailsTabs.tabs[TabIdxs.Payments].active = true);
        }

        // Loading data
        await this._loadPaymentBatch();

        if (payments) {
            await this.paymentBatchDetailsGrid.refresh();
        }
        if (status) {
            await this._loadPaymentBatchDetailsWorkflowStatus();
        }
    }

    async advanceTask() {
        try {
            const message = `Are you sure you wish to ${this.workflowStatus?.taskAdvanceName}`;
            await this._messageModalService.confirm(message, 'Confirm Completion');
        } catch (e) {
            return Promise.resolve(false);
        }

        if (this.workflowStatus.advanceCreatesPackages && this.paymentBatch.hasZeroAmountPayments) {
            try {
                const operation: string = this.isTransmittal ? 'Transmittal' : 'Feed generation';
                const message = `The batch has 1 or more payments with a $0 amount which will be excluded from the ${operation}. Are you sure you wish to proceed?`;
                await this._messageModalService.confirm(message, 'Confirm Proceeding');
            } catch (e) {
                return Promise.resolve(false);
            }
        }

        if (
            this.paymentBatch.batchType === Core.PaymentBatchTypeEnum.BillPay &&
            this.workflowStatus?.taskAdvanceName === 'Confirm Funding' &&
            (!this.paymentBatch.fundedAmount ||
                !this.paymentBatch.paymentAmount ||
                this.paymentBatch.fundedAmount < this.paymentBatch.paymentAmount)
        ) {
            try {
                const message = `The Funded Amount (${this.paymentBatch.fundedAmount}) is less than the total batch amount (${this.paymentBatch.paymentAmount}). Are you sure you wish to proceed?`;
                await this._messageModalService.prompt(message, 'Confirm Partial Funding');
            } catch (e) {
                return Promise.resolve(false);
            }
        }

        const updateModel = {
            paymentBatchId: this.paymentBatch.paymentBatchId,
            taskId: this.workflowStatus.taskID,
        };

        if (this.workflowStatus.advanceTaskAsLongRunning) {
            const lrpId = await this._paymentBatchService.startCompleteTask(
                this.paymentBatch.paymentBatchId,
                updateModel,
            );
            this._launchLRP(lrpId, 'Complete Task', 'Task completing', this._handleTaskChangeResult.bind(this));
        } else {
            const busyRef = this._busyIndicatorService.show({ message: 'Advancing task...' });
            try {
                const taskChangeResult = await this._paymentBatchService.advanceTask(
                    this.paymentBatch.paymentBatchId,
                    updateModel,
                );
                await this._handleTaskChangeResult(taskChangeResult);
            } finally {
                busyRef.hide();
            }
        }
    }

    async launchExceptionModal() {
        const params = {
            workflowStatus: this.workflowStatus,
            paymentBatchId: this.paymentBatch.paymentBatchId,
        };

        const result = await this._modalService.showAsync(PaymentBatchTaskExceptionModalComponent, params, 'modal-md');

        if (!result) return;

        if (result.updateResultModel) {
            const {
                refreshStatus: status,
                refreshPayments: payments,
                refreshPackages: packages,
                succeeded,
                errorMessage,
            } = result.updateResultModel;
            if (succeeded) {
                await this.refreshPage({ status, payments, packages });
            } else {
                // TODO handle result.PaymentErrors
                this._toastr.error(errorMessage);
            }
        } else if (result.longRunningProcessId) {
            const handleTaskChangeResultCB = ({
                refreshStatus: status,
                refreshPayments: payments,
                refreshPackages: packages,
            }: Core.PaymentBatchTaskUpdateResultModel) => {
                // Handle the result of the long running process
                this.refreshPage({ status, payments, packages });
            };

            this._launchLRP(result.longRunningProcessId, 'Rescind Task', 'Task Rescinding', handleTaskChangeResultCB);
        }
    }

    async recallBatch() {
        try {
            const message = `Are you sure you want to recall the batch #${this.paymentBatch.batchNo}`;
            await this._messageModalService.confirm(message, 'Confirm Recall');
        } catch (e) {
            return Promise.resolve(false);
        }

        const busyRef = this._busyIndicatorService.show({ message: 'Recalling' });

        try {
            const updateModel = {
                paymentBatchId: this.paymentBatch.paymentBatchId,
                taskId: this.workflowStatus.taskID,
            };
            const {
                refreshStatus: status,
                refreshPayments: payments,
                refreshPackages: packages,
            } = await this._paymentBatchService.rescindTask(this.paymentBatch.paymentBatchId, updateModel);

            await this.refreshPage({ status, payments, packages });
        } finally {
            busyRef.hide();
        }

        return Promise.resolve(true);
    }

    private async _loadPaymentBatch(): Promise<void> {
        const busyRef = this._busyIndicatorService.show({ message: 'Loading' });

        try {
            const paymentBatchId = +this._upgradeNavigationServiceHandler.getQuerystringParam('paymentBatchId');
            this.paymentBatch = await this._paymentBatchService.getPaymentBatchDetailById(paymentBatchId);
            this.attachmentModalData = {
                entityType: 'Payment',
                entityName: this.paymentBatch.description,
                disableYears: false,
                singleYear: false,
                entityData: {
                    typeId: Core.EntityTypes.PaymentBatch,
                    id: this.paymentBatch.paymentBatchId,
                    name: this.paymentBatch.description,
                },
                readOnly: false,
            } as AttachmentModalData;

            this.commentModalData = {
                entityID: this.paymentBatch.paymentBatchId,
                entityTypeID: Core.EntityTypes.PaymentBatch,
                entityName: this.paymentBatch.description,
                canEdit: true,
                description: null,
            } as CommentModalData;
            this.hasAttachments = this.paymentBatch.hasAttachments;
            this.hasComments = this.paymentBatch.hasComments;
        } finally {
            busyRef.hide();
        }
    }

    private async _loadPaymentBatchDetailsWorkflowStatus(): Promise<void> {
        this.workflowStatus = await this._paymentBatchService.getPaymentBatchDetailsWorkflowStatus(
            this.paymentBatch.paymentBatchId,
        );
    }

    private async _launchLRP(
        longRunningProcessId: number,
        title: string,
        message: string,
        handleTaskChangeResultCB: (result: Core.PaymentBatchTaskUpdateResultModel) => void,
    ): Promise<void> {
        const identifier = this._busyIndicatorService.generateUniqueMessageIdentifier();

        const busyConfig: IBusyIndicatorConfig = {
            identifier,
            longRunningProcessId,
            title,
            message,
            hasProgressBar: false,
        };

        const busyRef = this._busyIndicatorService.show(busyConfig);

        busyRef
            .onProgressBarComplete()
            .pipe(takeUntil(this.destroy$))
            .subscribe(async (succeeded: boolean) => {
                busyRef.hide();

                if (succeeded) {
                    const { result } = await this._longRunningProcessService.get(longRunningProcessId);
                    const { updateResult }: Core.PaymentBatchTaskUpdateLongRunningProcessResultModel =
                        JSON.parse(result);

                    handleTaskChangeResultCB(updateResult);
                }

                this.destroy$.next();
            });

        busyRef
            .onDismiss()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                busyRef.hide();
                this.destroy$.next();
            });
    }

    private async _handleTaskChangeResult({
        succeeded,
        refreshStatus: status,
        refreshPayments: payments,
        refreshPackages: packages,
        errorMessage,
        batchErrors,
        paymentErrors,
        packageIds,
    }: Core.PaymentBatchTaskUpdateResultModel): Promise<void> {
        if (succeeded) {
            await this.refreshPage({ status, payments, packages });

            // If this is a Transmittal advance, we know that draft Payment Packages have been
            // created, so we want to navigate to the Bill Payment Packages page to finalize.
            if (packageIds && packageIds.length) {
                const newKey = new Date().getTime();
                sessionStorage[`PaymentPackageDrafts${newKey}`] = JSON.stringify(packageIds);
                const params = {
                    draftID: newKey,
                    paymentBatchId: this.paymentBatch.paymentBatchId,
                    taskId: this.workflowStatus.taskID,
                } as any;
                if (this.workflowStatus.taskAdvanceName === 'Prepare Preliminary AP Feed') {
                    params.isPrelimApFeed = true;
                }
                this._routerService.go('paymentPackagesDraft', params);
            }
        } else {
            // Handle single Error message
            if (errorMessage) {
                await this.refreshPage({ packages: true, status: false, payments: false });
                this._toastr.error(errorMessage);
            }
            // Handle Payment-level Errors
            if (paymentErrors && paymentErrors.length) {
                this.paymentBatchDetailsGrid.addPaymentErrors(paymentErrors);
                const uniqueErrorPayments = uniqBy(paymentErrors, 'entityID');
                this._toastr.error(
                    'Hover over payment error icon for more details.',
                    `${uniqueErrorPayments.length} payments have errors.`,
                );
            }
            // Handle Batch-level Errors
            if (batchErrors && batchErrors.length) {
                const errorMessages: string[] = _.map(batchErrors, 'errorMessage');
                this._taskService.showErrorNotificationModal(
                    errorMessages,
                    [],
                    'The following error(s) were encountered attempting to advance the task:',
                    'The following warning(s) were encountered attempting to advance the task:',
                );
            }
        }
    }
}
